import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { VacationRequest, VacationRequestStatus } from "../../api/types";

const STATUS_LABELS: Record<VacationRequestStatus, string> = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

const STATUS_BADGE: Record<VacationRequestStatus, string> = {
  PENDING: "badge",
  APPROVED: "badge success",
  REJECTED: "badge danger",
  CANCELLED: "badge muted",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Self-service vacations on the profile page: request a date range,
 * see own requests, cancel pending or future-approved ones. Approval happens
 * on the Schedule page (owner/manager).
 */
export function VacationsCard() {
  const [rows, setRows] = useState<VacationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listMyVacationRequests());
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load vacations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.createVacationRequest({
        startDate,
        endDate,
        reason: reason.trim() || undefined,
      });
      setMessage("Vacation request submitted.");
      setReason("");
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not submit request.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.cancelVacationRequest(id);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not cancel request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: "grid", gap: "var(--space-3)" }}>
      <div>
        <strong style={{ fontSize: 16 }}>Vacations</strong>
        <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
          Request time off. Florist requests go to your shop manager or the
          owner; everyone else is approved by the owner.
        </p>
      </div>

      {message ? <div className="alert success">{message}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      <div
        style={{
          display: "flex",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div className="field">
          <label htmlFor="vacation-start">First day</label>
          <input
            id="vacation-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="vacation-end">Last day</label>
          <input
            id="vacation-end"
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field" style={{ flex: 1, minWidth: "14rem" }}>
          <label htmlFor="vacation-reason">Reason (optional)</label>
          <input
            id="vacation-reason"
            type="text"
            maxLength={500}
            value={reason}
            placeholder="e.g. family trip"
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
        </div>
        <button
          type="button"
          className="button"
          onClick={() => void submit()}
          disabled={busy || !startDate || !endDate}
        >
          {busy ? "Submitting…" : "Request vacation"}
        </button>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No vacation requests yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Dates</th>
              <th>Status</th>
              <th>Decided by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cancellable =
                r.status === "PENDING" ||
                (r.status === "APPROVED" &&
                  new Date(r.startDate).getTime() > Date.now());
              return (
                <tr key={r.id}>
                  <td>
                    {fmtDate(r.startDate)} – {fmtDate(r.endDate)}
                  </td>
                  <td>
                    <span className={STATUS_BADGE[r.status]}>
                      {STATUS_LABELS[r.status]}
                    </span>
                    {r.decisionNote ? (
                      <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                        {r.decisionNote}
                      </span>
                    ) : null}
                  </td>
                  <td>{r.decidedBy?.fullName ?? "—"}</td>
                  <td>
                    {cancellable ? (
                      <button
                        type="button"
                        className="button plain"
                        onClick={() => void cancel(r.id)}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
